import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download } from 'lucide-react'

import { ApiError, apiUrl } from '@/api/client'
import { usersApi } from '@/api/endpoints'
import { keys } from '@/api/keys'
import type { DataExport } from '@/api/types'
import { PageHeader } from '@/app/layouts/PageHeader'
import { Button } from '@/components/ui/Button'
import { ErrorState } from '@/components/ui/EmptyState'
import { CenteredSpinner } from '@/components/ui/Spinner'
import { useToast } from '@/components/ui/Toast'
import { BCP47, useI18n } from '@/i18n'
import { useFormat } from '@/lib/format'

/** 打包中每隔几秒看一眼状态；打好了就停 */
const POLL_MS = 4000

/**
 * 你的数据（D85）：申请导出 → 后台打包 → 7 天内下载。
 *
 * 下载用普通的 <a href>，靠 Cookie 鉴权：zip 可能很大，不该整个读进 JS 再转成 blob。
 */
export function SettingsDataPage() {
  const { t } = useI18n()
  const toast = useToast()
  const client = useQueryClient()
  const current = useQuery({
    queryKey: keys.dataExport(),
    queryFn: usersApi.latestExport,
    refetchInterval: (query) => (query.state.data?.status === 'pending' ? POLL_MS : false),
  })
  const request = useMutation({
    mutationFn: usersApi.requestExport,
    onSuccess: (fresh) => client.setQueryData(keys.dataExport(), fresh),
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : t('toast.actionFailed')),
  })

  return (
    <>
      <PageHeader title={t('dataExport.title')} back />
      <section className="space-y-3 border-b border-line px-4 py-4 text-[15px]">
        <p>{t('dataExport.intro')}</p>
        <ul className="list-disc space-y-1 pl-5 text-ink-muted">
          <li>{t('dataExport.includesContent')}</li>
          <li>{t('dataExport.includesSocial')}</li>
          <li>{t('dataExport.includesMessages')}</li>
          <li>{t('dataExport.includesMedia')}</li>
        </ul>
        <p className="text-[13px] text-ink-muted">{t('dataExport.noSecrets')}</p>
      </section>

      <section className="px-4 py-4">
        {current.isPending ? (
          <CenteredSpinner />
        ) : current.isError ? (
          <ErrorState message={current.error.message} onRetry={() => current.refetch()} />
        ) : (
          <ExportStatus
            export={current.data}
            requesting={request.isPending}
            onRequest={() => request.mutate()}
          />
        )}
      </section>
    </>
  )
}

function ExportStatus({
  export: current,
  requesting,
  onRequest,
}: {
  export: DataExport | null
  requesting: boolean
  onRequest: () => void
}) {
  const { t, locale } = useI18n()
  const { absoluteTime } = useFormat()

  const requestButton = (label: string) => (
    <Button onClick={onRequest} loading={requesting}>
      {label}
    </Button>
  )

  if (!current || current.status === 'expired') {
    return (
      <div className="space-y-3">
        {current && <p className="text-[15px] text-ink-muted">{t('dataExport.expired')}</p>}
        {requestButton(t('dataExport.request'))}
      </div>
    )
  }
  if (current.status === 'pending') {
    return (
      <p role="status" className="text-[15px]">
        {t('dataExport.pending')}
      </p>
    )
  }
  if (current.status === 'failed') {
    return (
      <div className="space-y-3">
        <p role="alert" className="text-[15px] text-danger">
          {t('dataExport.failed')}
        </p>
        {requestButton(t('dataExport.retry'))}
      </div>
    )
  }
  return (
    <div className="space-y-3">
      <p className="text-[15px]">
        {t('dataExport.ready', {
          size: formatBytes(current.size_bytes ?? 0, BCP47[locale]),
          date: current.expires_at ? absoluteTime(current.expires_at) : '',
        })}
      </p>
      <a
        href={apiUrl('/users/me/export/download')}
        download
        className="inline-flex h-9 items-center gap-2 px-corners bg-brand px-4 text-[15px] font-bold text-on-brand hover:bg-brand-hover"
      >
        <Download className="size-4" aria-hidden />
        {t('dataExport.download')}
      </a>
    </div>
  )
}

function formatBytes(bytes: number, locale: string): string {
  const [value, unit] =
    bytes >= 1024 * 1024
      ? [bytes / (1024 * 1024), 'megabyte']
      : [Math.max(bytes / 1024, 0.1), 'kilobyte']
  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit,
    unitDisplay: 'short',
    maximumFractionDigits: 1,
  }).format(value)
}
